import React from "react";
import { Field, Form, FormSpy } from 'react-final-form';
import { Box, Container, Link } from '@mui/material';
import Typography from '../../modules/components/Typography';
import { phone, required } from '../../modules/form/validation';
import FormButton from '../../modules/form/FormButton';
import FormFeedback from '../../modules/form/FormFeedback';
import { FORM_ERROR } from 'final-form';
import { getUser } from "../../modules/components/api";
import Paper from '../../modules/components/Paper';
import RFTextField from "../../modules/form/RFTextField";
import PhoneInput from 'react-phone-input-2';
import './materialize.css';
import { updateUserPhoneNumber } from "../../modules/components/api";
import { useOutletContext } from "react-router-dom";
import logger from "../../modules/components/logger";

const PhoneAdapter = ({ input, userPhone, disabled }) => {
  const [defaultValue, setDefaultValue] = React.useState(userPhone)
  return (
    <PhoneInput
      variant="standard"
      country='us'
      disabled={disabled}
      regions={['north-america', 'europe']}
      value={input.value.value || defaultValue || userPhone}
      onChange={value => {
        input.onChange(value);
      }}
      onClick={() => setDefaultValue("1")}
      default
    />
  )
};

const Warning = () => (
  <Typography variant="body2" align="justify" color="text.secondary" sx={{ mt: 2 }}>
    <p>By submitting your phone number, you are agreeing to receive SMS messages from Watch Exchanger about your account. With a phone number on file, you gain the ability to enable phone notifications for your watch subscriptions.</p>
    <p>When you enable phone notifications on a watch subscription, you're signing up to receive text notifications when matching watches are posted to the Watch Exchange subreddit.</p>
    <p>You can stop receiving SMS notifications at any time by deleting/disabling the related watch subscription.</p>
    <p>
      {`Watch Exchanger will never share your phone number with any third parties. `}
      <Link href="/dashboard/privacy" underline="always" color="text.secondary">
        Learn more.
      </Link>
    </p>
  </Typography>
);

const States = {
  NoPhone: "nophone",
  Verified: "verified",
  Pending: "pending"
};

export default function UpdateProfile() {
  const [isValid, setIsValid] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const [state, setState] = React.useState("");
  const [contextUserInfo,,] = useOutletContext();
  const [userInfo, setUserInfo] = React.useState(contextUserInfo);

  React.useEffect(() => {
    if (userInfo?.phoneValidated) {
      setState(States.Verified);
    }
    else {
      if (userInfo?.phoneValidationCode?.length === 6) {
        setState(States.Pending);
      }
      else {
        setState(States.NoPhone);
      }
    }
  }, [userInfo])

  const handleRefresh = async (form) => {
    setSubmitted(true);
    setTimeout(async () => {
      const res = await getUser(userInfo.id);
      const newUserInfo = res?.data?.getUserInfo;
      setUserInfo(newUserInfo);
      setSubmitted(false);
      setRefresh(!refresh);
      form.reset();
    }, 1000);
  };

  const validate = (values) => {
    const fields = [];
    if (userInfo?.phoneValidationCode?.length === 6)
      fields.push('phoneValidationCode');
    else
      fields.push('phone');
      
    const errors = required(fields, values);

    if (fields.includes('phone') && !errors.phone) {
      const phoneError = phone(values.phone);
      if (phoneError) {
        errors.phone = phoneError;
      }
      else if (values.phone === userInfo?.phone) {
        errors.phone = 'This is your current phone number';
      }
    }

    if (fields.includes('phoneValidationCode') && !errors.phoneValidationCode) {
      if (values?.phoneValidationCode?.length !== 6) {
        errors.phoneValidationCode = 'Invalid code';
      }
    }

    setIsValid(Object.keys(errors).length === 0);

    return errors;
  };

  const handleSubmit = ({ phone, phoneValidationCode }, form) => {
    if (state === States.NoPhone || state === States.Verified) {
      const phoneValidationCode = Math.floor(100000 + Math.random() * 900000).toString();
      return updateUserPhoneNumber(userInfo?.id, phone, phoneValidationCode, false)
        .then(() => {
          handleRefresh(form);
        })
        .catch((reason) => {
          const exception = reason?.errors[0]?.message;
          let message = "Error updating phone number.";
          if (exception.includes("invalid value"))
            message = "Phone number entered is not valid.";
          return {[FORM_ERROR]: message};
        });
    }
    else {
      if (userInfo?.phoneValidationCode === phoneValidationCode) {
        return updateUserPhoneNumber(userInfo?.id, phone, "", true)
          .then(() => {
            handleRefresh(form);
          })
          .catch((reason) => {
            const exception = reason?.errors[0]?.message;
            let message = "Error updating phone number.";
            if (exception.includes("invalid value"))
              message = "Phone number entered is not valid.";
            return {[FORM_ERROR]: message};
          });
      }
      else {
        return {[FORM_ERROR]: "Code is invalid."};
      }
    }
  };

  const handleClickReset = (form) => {
    return updateUserPhoneNumber(userInfo?.id, null, "", false)
      .then(() => {
        console.log(form);
        handleRefresh(form);
      })
      .catch(logger.errror);
  }

  return (
    <React.Fragment>
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper
            background="light"
            sx={{ py: { xs: 4, md: 8 }, px: { xs: 3, md: 6 } }}
          >
            <React.Fragment>
              <Typography variant="h3" gutterBottom marked="center" align="center">
                Update Contact Info
              </Typography>
            </React.Fragment>
            <Form
              onSubmit={handleSubmit}
              subscription={{ dirty: true }}
              validate={validate}
            >
              {({ handleSubmit: handleSubmit2, dirty, form }) => (
                <Box 
                  component="form" 
                  onSubmit={(props) => handleSubmit2(props, form)} 
                  noValidate 
                  sx={{ mt: 6 }}
                >
                  <Field
                    autoComplete="email"
                    component={RFTextField}
                    disabled
                    initialValue={userInfo?.email}
                    fullWidth
                    label="Email"
                    margin="normal"
                    name="email"
                    size="large"
                  />
                  <Field
                    autoComplete="phone"
                    component={PhoneAdapter}
                    disabled={submitted || userInfo?.phoneValidationCode?.length === 6}
                    fullWidth
                    margin="normal"
                    name="phone"
                    size="large"
                    userPhone={userInfo?.phone ?? ""}
                  />
                  {state === States.Verified && 
                    <Typography variant="body2" align="justify" color="text.secondary" sx={{ mt: 2 }}>

                      <p>This phone number is verified. You can change it by entering a new phone number above and reverifying it.</p>
                    </Typography>}
                  {state === States.Pending && <>
                      <Typography variant="body2" align="justify" color="text.secondary" sx={{ mt: 2 }}>
                        <p>Your phone number has been updated. Please check your phone for a validation code, and enter it below.</p>
                      </Typography>
                      <Field
                        autoComplete="verficication code"
                        component={RFTextField}
                        required
                        disabled={submitted}
                        fullWidth
                        label="Validation Code"
                        margin="normal"
                        name="phoneValidationCode"
                        size="large"
                        type="number"
                      />
                    </>}
                  {!userInfo?.phoneAcknowledged && 
                    <Warning />
                  }
                  <FormSpy subscription={{ submitError: true }}>
                    {({ submitError }) =>
                      submitError ? (
                        <FormFeedback error sx={{ mt: 2 }}>
                          {submitError}
                        </FormFeedback>
                      ) : null
                    }
                  </FormSpy>
                  <FormButton
                    sx={{ mt: 3, mb: 2 }}
                    disabled={!isValid || !dirty || submitted}
                    size="large"
                    color="secondary"
                    fullWidth
                  >
                    {submitted ? 'In progress…' : 
                      state === States.Pending ? 'Submit Code' : 'Update Phone Number'
                    }
                  </FormButton>
                  {state === States.Pending &&
                    <React.Fragment>
                      <Typography variant="body2" align="center" color="text.secondary">
                        {"If it's been more than a few minutes, "}
                        <Link 
                          component="button"
                          underline="always" 
                          onClick={() => handleClickReset(form)}
                          variant="body2"
                          color="text.secondary"
                          style={{paddingBottom: "2px"}}
                        >
                          click here to reset.
                        </Link>
                      </Typography>
                    </React.Fragment>}
                </Box>
              )}
            </Form>
          </Paper>
        </Box>
      </Container>
    </React.Fragment>
  );
}